import { useTranslation } from "../../../i18n/useTranslation";

/** m:ss, from a DERIVED difference. Never a duration authored here. */
export function waitLabel(msLeft: number): string {
  const total = Math.max(0, Math.ceil(msLeft / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/** YYYY-MM-DD of an instant in a timezone, for the "today" comparison. */
function dayIn(ms: number, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(ms));
}

/**
 * "Finish booking by 10:04 AM today" — the hold, in the only words that mean
 * anything to the person reading it (web v2 §05). The instant is the hold's own
 * `expiresAt`, shown in the professional's timezone like every other time in
 * the flow, and "today" is DERIVED: it appears only when the deadline falls on
 * the displayed day in that timezone, and the date is named when it does not.
 */
export function FinishBy({
  expiresAt,
  timeZone,
  now,
  summary,
  testId = "guest-finish-by",
}: {
  expiresAt: string | null;
  timeZone: string;
  now: number;
  /**
   * What is being booked, already formatted by the flow ("Cut and finish ·
   * Thu, Oct 8, 2:30 PM"). Web v2 §05 carries it in this box on the code and
   * change-email steps, so the person confirming an address can still see what
   * the code is for. Absent in the harness's minimal cases.
   */
  summary?: string;
  testId?: string;
}) {
  const { t, fmt } = useTranslation();
  const valid = !!expiresAt && Number.isFinite(Date.parse(expiresAt));
  if (!valid && !summary) return null;
  let deadline: string | null = null;
  if (valid && expiresAt) {
    const time = fmt.time(expiresAt, timeZone);
    const sameDay = dayIn(Date.parse(expiresAt), timeZone) === dayIn(now, timeZone);
    deadline = sameDay
      ? t("guest.hold.finishByToday", { time })
      : t("guest.hold.finishByOn", { time, date: fmt.date(expiresAt, timeZone) });
  }
  return (
    <div className="b-notice t-quiet" data-testid={testId}>
      <div className="b-notice-b">
        {summary ? <b data-testid="guest-summary">{summary}</b> : null}
        {deadline ? <p className="b-p">{deadline}</p> : null}
      </div>
    </div>
  );
}
